/** ORDER 48 — bounded eager-history policy, kept pure so its limits and stop rules are armable. */

import type { Rung } from "./rungLadder.ts";

export const EAGER_HISTORY_MIN_SESSIONS = 5;
export const EAGER_HISTORY_SESSION_MINUTES = 390;
export const EAGER_HISTORY_PAGE_BARS = 300;
export const EAGER_HISTORY_MAX_BARS = 2_000;
export const EAGER_HISTORY_EMPTY_RATIO = 0.30;

const EAGER_HISTORY_TARGET_SPAN_MS =
  EAGER_HISTORY_MIN_SESSIONS * EAGER_HISTORY_SESSION_MINUTES * 60_000;

export type HistoryPageResult = "loaded" | "exhausted" | "failed" | "stale";

/**
 * The final page may cross the approximate cap by at most one page minus one bar. This is
 * deliberate: flooring at 240 live + five pages stops at 1,740 and misses five 390-bar RTH
 * sessions; six pages lands at 2,040, the smallest page-aligned population that covers them.
 */
export function eagerHistoryPageBudget(loadedBars: number): number {
  if (!Number.isFinite(loadedBars) || loadedBars < 0) return 0;
  if (loadedBars >= EAGER_HISTORY_MAX_BARS) return 0;
  return Math.ceil((EAGER_HISTORY_MAX_BARS - Math.floor(loadedBars)) / EAGER_HISTORY_PAGE_BARS);
}

/** Minute rungs have a CVD cursor door; sub-minute history is not served and is never invented. */
export function needsEagerHistory(
  rung: Rung,
  loadedBars: number,
  oldestMs: number | null,
  newestMs: number | null,
  pagesLoaded: number,
): boolean {
  if (rung.kind !== "tf" || eagerHistoryPageBudget(loadedBars) === 0) return false;
  if (oldestMs === null || newestMs === null || !Number.isFinite(oldestMs) || !Number.isFinite(newestMs)) {
    return false;
  }
  // Every eligible mount proves at least one continuation page. Thereafter the measured span,
  // not an assumed bars-per-session count, decides whether the five-session floor is covered.
  return pagesLoaded === 0 || newestMs - oldestMs < EAGER_HISTORY_TARGET_SPAN_MS;
}

/** Fraction of a requested visible time range that lies outside the loaded time domain. */
export function shouldFetchForEmptyRange(
  window: { from: number; to: number } | null,
  loadedFrom: number | null,
  loadedTo: number | null,
): boolean {
  if (window === null || loadedFrom === null || loadedTo === null) return false;
  if (![window.from, window.to, loadedFrom, loadedTo].every(Number.isFinite) || window.to <= window.from) {
    return false;
  }
  const emptyLeft = Math.max(0, Math.min(window.to, loadedFrom) - window.from);
  const emptyRight = Math.max(0, window.to - Math.max(window.from, loadedTo));
  return (emptyLeft + emptyRight) / (window.to - window.from) > EAGER_HISTORY_EMPTY_RATIO;
}

/** Await each aligned page frame before asking for the next; any refusal ends this eager run. */
export async function runSequentialHistoryFill(
  pageBudget: number,
  loadPage: () => Promise<HistoryPageResult>,
): Promise<{ pagesLoaded: number; stopped: HistoryPageResult | "budget" }> {
  const boundedBudget = Math.max(0, Math.floor(pageBudget));
  let pagesLoaded = 0;
  for (let page = 0; page < boundedBudget; page += 1) {
    const result = await loadPage();
    if (result !== "loaded") return { pagesLoaded, stopped: result };
    pagesLoaded += 1;
  }
  return { pagesLoaded, stopped: "budget" };
}
